import React, { useEffect, useRef, useState } from 'react';
import { Box, Button } from '@mui/material';
import { MillosMark } from '../icons';
import { marketingColors as C, marketingFonts as F, marketingDarkPalette as D, marketingDarkStyles, withOpacity } from '../../../theme_landing_page_2';

/**
 * "Option A" loop diagram: MillOS asks two parties for availability, overlays their calendars,
 * confirms with the landlord, then files the result and restarts on a countdown. Same shape for
 * both agents (Certificates: provider+tenant calendars → booked certificate; Accounting: bank
 * feed+receipts → filed return), content supplied per `agent`. Adaptation of Claude Design's
 * "RentoraMillChapter Option A" diagram (inlined on the compliance chapter) and its sibling
 * "RentoraLoopAcct" component (the accounting chapter) — unified into one responsive component
 * (ResizeObserver scale-to-fit, like MillosLoop/MillosPricingHero) since the original compliance
 * treatment had no scale wrapper and would have overflowed on narrow screens.
 */

const KEYFRAMES = `
@keyframes rlHalo{0%,100%{opacity:.25;transform:scale(1)}50%{opacity:.6;transform:scale(1.14)}}
@keyframes rlBreathe{0%,100%{transform:scale(1)}50%{transform:scale(1.06)}}
@keyframes rlWireA{0%,6%{stroke-dashoffset:200}16%,74%{stroke-dashoffset:0}86%,100%{stroke-dashoffset:200}}
@keyframes rlE{0%,0%{opacity:0;transform:translateY(8px)}18%,96%{opacity:1;transform:none}99%,100%{opacity:0;transform:translateY(-3px)}}
@media (prefers-reduced-motion:reduce){[data-renewal-loop] *{animation:none!important}}
[data-renewal-loop][data-paused="true"] [data-loop-canvas] *{animation-play-state:paused!important}
`;

interface Day {
  letter: string;
  mark: string;
  bg: string;
  border: string;
  color: string;
}

function calDays(open: number[]): Day[] {
  const L = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  return L.map((letter, i) =>
    open.includes(i)
      ? { letter, mark: '✓', bg: C.mist, border: C.hairline, color: D.accentOnLight }
      : { letter, mark: '', bg: C.white, border: C.hairline, color: C.muted }
  );
}

interface Party {
  role: string;
  name: string;
  top: number;
  note: string;
  days: Day[];
}

interface Content {
  askLabel: React.ReactNode;
  parties: [Party, Party];
  matchLabel: React.ReactNode;
  matchChip: string;
  confirmKicker: string;
  confirmTitle: string;
  confirmBody: string;
  inboxKicker: string;
  inboxTitle: string;
  inboxBody: string;
  filedKicker: string;
  filedTitle: string;
  filedFootLabel: string;
  filedFootValue: string;
  restartKicker: string;
  restartTitle: string;
  restartBody: string;
}

const CONTENT: Record<'compliance' | 'accounting', Content> = {
  compliance: {
    askLabel: (
      <>
        asks both
        <br />
        for dates
      </>
    ),
    parties: [
      { role: 'Provider', name: 'James · Gas Safe', top: 60, note: 'Offers Thursday or Saturday', days: calDays([3, 5]) },
      { role: 'Tenant', name: 'Sarah · Flat 2', top: 272, note: 'Free Friday or Saturday', days: calDays([4, 5]) },
    ],
    matchLabel: (
      <>
        Only day
        <br />
        both are free
      </>
    ),
    matchChip: 'Sat 19th',
    confirmKicker: 'You · confirmed',
    confirmTitle: 'Calendar invite sent',
    confirmBody: 'To James, Sarah and you. Booking confirmed — nothing to approve.',
    inboxKicker: 'In your inbox',
    inboxTitle: 'New certificate attached',
    inboxBody: 'MillOS takes it from the email — you never open it.',
    filedKicker: 'Filed in your vault',
    filedTitle: 'Stored against Flat 2',
    filedFootLabel: 'Valid until',
    filedFootValue: '19 Sep 2027',
    restartKicker: '90 days before expiry',
    restartTitle: 'MillOS starts again',
    restartBody: 'Same loop, next renewal. You are never the trigger.',
  },
  accounting: {
    askLabel: (
      <>
        reads bank
        <br />
        and receipts
      </>
    ),
    parties: [
      { role: 'Bank feed', name: 'Monzo · read-only', top: 60, note: 'Rent lands, matched daily', days: calDays([0, 2, 4]) },
      { role: 'Receipts', name: 'Invoices · email', top: 272, note: 'Filed as they arrive', days: calDays([1, 3]) },
    ],
    matchLabel: (
      <>
        Matched
        <br />
        to the right flat
      </>
    ),
    matchChip: '8 of 8',
    confirmKicker: 'Q3 figures',
    confirmTitle: 'Prepared for your accountant',
    confirmBody: 'Property income and expenses brought together for review.',
    inboxKicker: 'To your accountant',
    inboxTitle: 'Quarterly figures ready',
    inboxBody: 'Prepared to share with your accountant, with supporting records.',
    filedKicker: 'Recorded',
    filedTitle: 'Quarterly figures · Q3',
    filedFootLabel: 'Prepared',
    filedFootValue: '22 Jan 2027',
    restartKicker: 'Next quarter opens',
    restartTitle: 'MillOS starts again',
    restartBody: 'Same loop, next quarter. You are never the trigger.',
  },
};

const WIRES = [
  { d: 'M112 186 C 150 150, 152 104, 186 100', color: D.blue },
  { d: 'M112 226 C 150 262, 152 308, 186 312', color: D.blue },
  { d: 'M406 100 C 442 104, 448 158, 452 194', color: D.blueSoft },
  { d: 'M406 312 C 442 308, 448 254, 452 218', color: D.blueSoft },
  { d: 'M530 206 L 596 206', color: D.accent },
  { d: 'M784 206 L 836 206', color: D.accent },
];

const LOOP_WIRE = 'M918 272 C 918 390, 918 470, 850 470 L 150 470 C 40 470, -16 442, -16 206 L 12 206';

interface Props {
  agent: 'compliance' | 'accounting';
}

const MillosRenewalLoop: React.FC<Props> = ({ agent }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [width, setWidth] = useState(1000);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const fit = () => {
      const el = wrapRef.current;
      if (!el) return;
      const w = el.getBoundingClientRect().width;
      if (w > 0) {
        setScale(Math.min(1, w / 1000));
        setWidth(w);
      }
    };
    fit();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null;
    if (ro && wrapRef.current) ro.observe(wrapRef.current);
    return () => ro?.disconnect();
  }, []);

  const content = CONTENT[agent];
  const boxH = Math.round(584 * scale);
  const boxL = Math.max(0, Math.round((width - 1000 * scale) / 2));

  return (
    <Box
      data-renewal-loop
      data-paused={paused}
      sx={{
        ...marketingDarkStyles,
        fontFamily: F.body,
        position: 'relative',
        overflow: 'hidden',
        background: D.surface,
        border: `1px solid ${withOpacity(D.muted, 0.25)}`,
        borderRadius: '28px',
        padding: { xs: '24px 16px', md: '40px 28px 34px' },
      }}
    >
      <style>{KEYFRAMES}</style>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button type="button" onClick={() => setPaused(value => !value)} sx={{ color: D.text, border: `1px solid ${D.muted}`, borderRadius: '24px', px: 2 }}>
          {paused ? 'Resume accounting animation' : 'Pause accounting animation'}
        </Button>
      </Box>
      <Box role="region" aria-label={agent === 'accounting' ? 'Accounting workflow diagram' : 'Certificate scheduling diagram'} tabIndex={0} sx={{ overflowX: 'auto', '&:focus-visible': { outline: `2px solid ${D.accent}`, outlineOffset: 2 } }}>
      <Box ref={wrapRef} sx={{ position: 'relative', width: '100%', minWidth: 900, height: boxH, maxWidth: 1000, margin: '0 auto' }}>
        <Box data-loop-canvas sx={{ position: 'absolute', left: boxL, top: 0, width: 1000, height: 540, transformOrigin: '0 0', transform: `scale(${scale})` }}>
          <svg viewBox="0 0 1000 540" preserveAspectRatio="xMidYMid meet" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible' }} fill="none">
            {WIRES.map((w, i) => (
              <path key={i} d={w.d} stroke={w.color} strokeWidth={2} strokeDasharray={200} style={{ animation: 'rlWireA 14s linear infinite' }} />
            ))}
            <path d={LOOP_WIRE} stroke={D.accent} strokeWidth={2} strokeDasharray={200} style={{ animation: 'rlWireA 14s linear infinite' }} />
          </svg>

          <Box sx={{ position: 'relative', height: 540 }}>
            {/* 1 · AI asks */}
            <Box sx={{ position: 'absolute', left: 0, top: 170, width: 112, textAlign: 'center' }}>
              <Box sx={{ position: 'relative', width: 72, height: 72, margin: '0 auto', display: 'grid', placeItems: 'center' }}>
                <Box component="span" sx={{ position: 'absolute', inset: -10, borderRadius: '50%', background: `radial-gradient(circle,${withOpacity(D.blue, 0.4)},transparent 70%)`, animation: 'rlHalo 3.4s ease-in-out infinite' }} />
                <Box sx={{ position: 'relative', width: 72, height: 72, borderRadius: '50%', background: D.elevated, border: `1px solid ${D.blue}`, display: 'grid', placeItems: 'center', animation: 'rlBreathe 3.4s ease-in-out infinite' }}>
                  <MillosMark size={34} />
                </Box>
              </Box>
              <Box sx={{ fontFamily: F.ui, fontWeight: 700, fontSize: 10.5, letterSpacing: '0.09em', textTransform: 'uppercase', color: D.blue, mt: '11px' }}>MillOS</Box>
              <Box sx={{ fontSize: 12, color: D.text, mt: '3px', lineHeight: 1.35 }}>{content.askLabel}</Box>
            </Box>

            {/* 2 · the two parties */}
            {content.parties.map((p) => (
              <Box key={p.role} sx={{ position: 'absolute', left: 186, top: p.top, width: 220, background: C.white, border: `1px solid ${C.hairline}`, borderRadius: '16px', padding: '14px 16px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '9px' }}>
                  <Box component="span" sx={{ fontFamily: F.ui, fontSize: 10, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: D.accentOnLight }}>{p.role}</Box>
                  <Box component="span" sx={{ fontSize: 11, color: C.body }}>{p.name}</Box>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '4px' }}>
                  {p.days.map((d, i) => (
                    <Box key={i} sx={{ textAlign: 'center' }}>
                      <Box sx={{ fontSize: 8.5, fontWeight: 600, color: C.muted, mb: '3px' }}>{d.letter}</Box>
                      <Box sx={{ height: 22, borderRadius: '6px', background: d.bg, border: `1px solid ${d.border}`, display: 'grid', placeItems: 'center', fontSize: 10, fontWeight: 700, color: d.color }}>{d.mark}</Box>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ fontSize: 11.5, color: C.body, mt: '9px' }}>{p.note}</Box>
              </Box>
            ))}

            {/* 3 · AI overlays */}
            <Box sx={{ position: 'absolute', left: 452, top: 150, width: 78, textAlign: 'center' }}>
              <Box sx={{ position: 'relative', width: 64, height: 64, margin: '0 auto', display: 'grid', placeItems: 'center' }}>
                <Box component="span" sx={{ position: 'absolute', inset: -9, borderRadius: '50%', background: `radial-gradient(circle,${withOpacity(D.accent, 0.45)},transparent 70%)` }} />
                <Box sx={{ position: 'relative', width: 64, height: 64, borderRadius: '50%', background: D.elevated, border: `1px solid ${D.accent}`, display: 'grid', placeItems: 'center' }}>
                  <MillosMark size={30} />
                </Box>
              </Box>
              <Box sx={{ fontFamily: F.ui, fontWeight: 700, fontSize: 10, letterSpacing: '0.09em', textTransform: 'uppercase', color: D.accent, mt: '10px' }}>{content.matchLabel}</Box>
              <Box sx={{ display: 'inline-block', mt: '8px', background: D.accent, color: D.surface, borderRadius: '20px', padding: '5px 12px', fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap' }}>{content.matchChip}</Box>
            </Box>

            {/* 4 · confirmation */}
            <Box sx={{ position: 'absolute', left: 596, top: 148, width: 188, background: D.elevated, border: `1px solid ${D.accent}`, borderRadius: '16px', padding: '15px 17px' }}>
              <Box sx={{ fontFamily: F.ui, fontSize: 10, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: D.accent, mb: '7px' }}>{content.confirmKicker}</Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '8px' }}>
                <Box component="span" sx={{ width: 24, height: 24, borderRadius: '7px', background: D.accent, display: 'grid', placeItems: 'center', flex: 'none' }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={D.surface} strokeWidth={3} strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 5 5L20 7" /></svg>
                </Box>
                <Box component="span" sx={{ fontSize: 13.5, fontWeight: 600, color: D.text, lineHeight: 1.25 }}>{content.confirmTitle}</Box>
              </Box>
              <Box sx={{ fontSize: 11.5, color: D.muted, lineHeight: 1.45 }}>{content.confirmBody}</Box>
            </Box>

            {/* 5 · inbox */}
            <Box sx={{ position: 'absolute', left: 836, top: 148, width: 164, background: C.white, border: `1px solid ${C.hairline}`, borderRadius: '16px', padding: '15px 16px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '9px' }}>
                <Box component="span" sx={{ width: 24, height: 24, borderRadius: '7px', background: C.mist, display: 'grid', placeItems: 'center', flex: 'none' }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={D.accentOnLight} strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"><path d="M22 6 12 13 2 6" /><rect x="2" y="4" width="20" height="16" rx="2" /></svg>
                </Box>
                <Box component="span" sx={{ fontFamily: F.ui, fontSize: 10, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: D.accentOnLight }}>{content.inboxKicker}</Box>
              </Box>
              <Box sx={{ fontSize: 13.5, fontWeight: 600, color: C.ink, lineHeight: 1.3 }}>{content.inboxTitle}</Box>
              <Box sx={{ fontSize: 11.5, color: C.body, mt: '4px', lineHeight: 1.45 }}>{content.inboxBody}</Box>
            </Box>

            {/* 6 · filed */}
            <Box sx={{ position: 'absolute', left: 588, top: 428, width: 212, background: C.white, border: `1px solid ${C.hairline}`, borderRadius: '16px', padding: '14px 16px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '8px' }}>
                <Box component="span" sx={{ width: 24, height: 24, borderRadius: '7px', background: C.mist, display: 'grid', placeItems: 'center', flex: 'none' }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={D.accentOnLight} strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 7H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1z" />
                    <path d="M5 7v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V7" />
                    <path d="M10 12h4" />
                  </svg>
                </Box>
                <Box component="span" sx={{ fontFamily: F.ui, fontSize: 10, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: D.accentOnLight }}>{content.filedKicker}</Box>
              </Box>
              <Box sx={{ fontSize: 13.5, fontWeight: 600, color: C.ink, lineHeight: 1.3 }}>{content.filedTitle}</Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: '9px', background: C.mist, border: `1px solid ${C.hairline}`, borderRadius: '9px', padding: '7px 10px' }}>
                <Box component="span" sx={{ fontSize: 11, color: C.body }}>{content.filedFootLabel}</Box>
                <Box component="span" sx={{ fontSize: 11.5, fontWeight: 700, color: C.ink }}>{content.filedFootValue}</Box>
              </Box>
            </Box>

            {/* 7 · restart */}
            <Box sx={{ position: 'absolute', left: 214, top: 428, width: 216, background: D.elevated, border: `1px solid ${D.accent}`, borderRadius: '16px', padding: '14px 16px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '8px' }}>
                <Box component="span" sx={{ width: 24, height: 24, borderRadius: '7px', background: D.accent, display: 'grid', placeItems: 'center', flex: 'none' }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={D.surface} strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
                </Box>
                <Box component="span" sx={{ fontFamily: F.ui, fontSize: 10, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: D.accent }}>{content.restartKicker}</Box>
              </Box>
              <Box sx={{ fontSize: 13.5, fontWeight: 600, color: D.text, lineHeight: 1.3 }}>{content.restartTitle}</Box>
              <Box sx={{ fontSize: 11.5, color: D.muted, mt: '4px', lineHeight: 1.45 }}>{content.restartBody}</Box>
            </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MillosRenewalLoop;
